'use client';

import type { CuratedLiveSplitpaneWidgetData } from '@weberus/shared-types';
import { useMemo, useState, type FC } from 'react';
import { InView } from 'react-intersection-observer';
import dynamic from 'next/dynamic';
import { cn } from '@/app/_shared/utils/tw-cn';
import { BottomArea } from './bottom-area';
import { BadgeArea } from './badge-area';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

type PaneItem = CuratedLiveSplitpaneWidgetData['items'][0];

export interface PaneItemProps extends PaneItem {
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const RIGHT_THRESHOLD = 200;
const LEFT_THRESHOLD = 120;
const HEIGHT_THRESHOLD = 300;

/**
 *  야놀자 라이브 스플릿패널의 아이템 컴포넌트
 *
 *  @see {@link https://www.figma.com/design/zlHuiOHmYv1ghVDbTHUSvW/%5BGUIDE%5D-%EC%9C%84%EC%A0%AF%2F%EC%98%A4%EB%8D%94%EB%A7%81?node-id=434-74460&m=dev}
 */
export const PaneItem: FC<PaneItemProps> = (props) => {
  const { action, image, type, videoPreview, onClick, product } = props;
  const [playing, setPlaying] = useState(false);
  const [isVideoError, setIsVideoError] = useState(false);

  const showBottomArea = type === 'PREVIEW' || !!product;
  const hasVideo = useMemo(() => {
    return (type === 'VOD' || type === 'LIVE' || type === 'CATCHUP') && !!videoPreview?.url;
  }, [type, videoPreview?.url]);

  const onIntersectionChange = (
    _: boolean,
    { boundingClientRect, intersectionRect }: IntersectionObserverEntry,
  ): void => {
    if (!hasVideo) return;

    const isLeftItem = boundingClientRect.left < 0;
    const threshold = isLeftItem ? LEFT_THRESHOLD : RIGHT_THRESHOLD;

    const { height, width } = intersectionRect;
    if (!playing && height > HEIGHT_THRESHOLD && width >= threshold) {
      setPlaying(true);
    } else if (playing && (height < HEIGHT_THRESHOLD || width < threshold)) {
      setPlaying(false);
    }
  };

  return (
    <InView threshold={[0.1667, 0.5, 0.8334]} onChange={onIntersectionChange}>
      <a
        href={action.web}
        className="relative block h-[360px] w-[240px] overflow-hidden rounded-12"
        onClick={(e) => onClick?.(e)}>
        {hasVideo && (
          <div className="*:*:object-cover">
            <ReactPlayer
              style={{ position: 'absolute', inset: 0 }}
              url={videoPreview?.url}
              width="100%"
              height="100%"
              config={{ file: { attributes: { preload: 'none' } } }}
              playing={playing && !isVideoError}
              playsinline
              muted
              loop
              onError={() => {
                setIsVideoError(true);
              }}
            />
          </div>
        )}
        {/* [주의] Safari 랜더링 이슈로 will-change 필요 */}
        <div
          style={{ backgroundImage: `url('${image}')` }}
          className={cn(
            'absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-300 will-change-[opacity]',
            playing && !isVideoError ? 'opacity-0' : 'opacity-100',
          )}
        />
        {showBottomArea && (
          <div
            className="absolute inset-0 will-change-[opacity]"
            style={{ background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 40%, rgba(0, 0, 0, 0.60) 80%)' }}
          />
        )}
        <BadgeArea {...props} />
        {showBottomArea && <BottomArea {...props} />}
      </a>
    </InView>
  );
};
